/**
 * AdminProtectedRoute.tsx
 * 
 * Protected route component for admin pages that checks authentication
 * and redirects to admin login if not authenticated.
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAdmin } from '../contexts/AdminContext'

interface AdminProtectedRouteProps {
  children: React.ReactNode
  requiredPermission?: string
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ 
  children, 
  requiredPermission 
}) => {
  const { state, hasPermission } = useAdmin()
  const location = useLocation()

  // Check if admin is authenticated
  if (!state.isAuthenticated || !state.user) {
    // Redirect to admin login with return URL
    return <Navigate to="/admin/login" state={{ from: location }} replace />
  }

  // Check if admin has required permission
  if (requiredPermission && !hasPermission(requiredPermission as any)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. Please contact your administrator.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default AdminProtectedRoute 